import React from 'react';
import { Row, Col, Input, Modal, ModalHeader, ModalBody } from 'reactstrap';
import {
  FOB_INCOTERM_ID,
  INCOTERM_PORT_OPTIONS,
  MULTIPORT_OPTIONS,
  TIMELINE_OPTIONS,
} from '../shared/constants';
import { formatParcelSize } from '../shared/utils';
import {
  CounterProductAlert,
  CounterModalLoading,
  CounterModalFooter,
  CounterFieldError,
  useLocationBlockRenderer,
} from '../shared/counterOfferShared';
import {
  CARGO_RATIO_LABELS,
  getCargoRatioKey,
} from '../../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';
import '../../../../TenderOpening/components/Detail/HpsParcel/lpg/CargoRatioSelector.css';

const RATIO_OPTIONS = CARGO_RATIO_LABELS.map((label, index) => ({
  id: index + 1,
  label,
}));

const isRatioSelected = (selectedRatios, option) =>
  selectedRatios.some(
    ratio =>
      getCargoRatioKey(ratio.id) === getCargoRatioKey(option.id) ||
      (ratio.label && ratio.label === option.label)
  );

const CounterOfferModalLpg = ({
  isOpen,
  toggle,
  formData,
  onFormChange,
  onSave,
  loading,
  saveDisabled = false,
  errors = {},
  form,
  requiresCargoRatio = false,
  incotermTypeOptions,
  incotermRouteOptions: _incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
}) => {
  const renderLocationBlock = useLocationBlockRenderer({
    incotermLocationOptions,
    onFormChange,
    errors,
  });

  const selectedRatios = formData.selectedRatios || [];

  const handleToggleRatio = option => {
    const ratioKey = getCargoRatioKey(option.id);
    const exists = selectedRatios.some(
      item => getCargoRatioKey(item.id) === ratioKey
    );
    const next = exists
      ? selectedRatios.filter(item => getCargoRatioKey(item.id) !== ratioKey)
      : [...selectedRatios, option];
    onFormChange('selectedRatios', next);
  };

  const isFobIncoterm = formData.incotermId === FOB_INCOTERM_ID;

  const showMultiPortOption =
    !isFobIncoterm && formData.incotermPortType === 'MultiPort';

  const showLocation2 =
    !isFobIncoterm &&
    formData.incotermPortType === 'MultiPort' &&
    (formData.incotermMultiPortType === '2Port' ||
      formData.incotermMultiPortType === '3Port');

  const showLocation3 =
    !isFobIncoterm &&
    formData.incotermPortType === 'MultiPort' &&
    formData.incotermMultiPortType === '3Port';

  const timelineLabel =
    TIMELINE_OPTIONS.find(option => option.value === formData.timelineType)
      ?.label || 'Accepted Discharged Dates';

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop='static'
      size='xl'
      scrollable
    >
      <ModalHeader toggle={toggle}>Add Counter Requirement (LPG)</ModalHeader>
      <ModalBody className='p-4'>
        <CounterProductAlert form={form} />

        {loading ? (
          <CounterModalLoading />
        ) : (
          <>
            <Row className='g-3 mb-4'>
              <Col md={4}>
                <div className='form-group'>
                  <label className='fw-semibold text-muted fs-13 mb-1'>
                    Type
                  </label>
                  <Input
                    type='select'
                    value={formData.incotermId}
                    onChange={e => onFormChange('incotermId', e.target.value)}
                    className='py-2 text-dark fw-medium form-select'
                    invalid={!!errors.incotermId}
                  >
                    <option value=''>Please select</option>
                    {incotermTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  <CounterFieldError message={errors.incotermId} />
                </div>
              </Col>

              {formData.incotermId !== FOB_INCOTERM_ID && (
                <Col md={4}>
                  <div className='form-group'>
                    <label className='fw-semibold text-muted fs-13 mb-1'>
                      Port(s)
                    </label>
                    <Input
                      type='select'
                      value={formData.incotermPortType}
                      onChange={e =>
                        onFormChange('incotermPortType', e.target.value)
                      }
                      className='py-2 text-dark fw-medium form-select'
                      invalid={!!errors.incotermPortType}
                    >
                      <option value=''>Please select</option>
                      {INCOTERM_PORT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <CounterFieldError message={errors.incotermPortType} />
                  </div>
                </Col>
              )}

              {formData.incotermId === FOB_INCOTERM_ID && (
                <Col md={4}>
                  <div className='form-group'>
                    <label className='fw-semibold text-muted fs-13 mb-1'>
                      Route
                    </label>
                    <Input
                      type='select'
                      value={formData.incotermRouteId}
                      onChange={e =>
                        onFormChange('incotermRouteId', e.target.value)
                      }
                      className='py-2 text-dark fw-medium form-select'
                      invalid={!!errors.incotermRouteId}
                    >
                      <option value=''>Please select</option>
                      {fobRouteOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <CounterFieldError message={errors.incotermRouteId} />
                  </div>
                </Col>
              )}

              {showMultiPortOption && (
                <Col md={4}>
                  <div className='form-group'>
                    <label className='fw-semibold text-muted fs-13 mb-1'>
                      Multiport Option
                    </label>
                    <Input
                      type='select'
                      value={formData.incotermMultiPortType}
                      onChange={e =>
                        onFormChange('incotermMultiPortType', e.target.value)
                      }
                      className='py-2 text-dark fw-medium form-select'
                      invalid={!!errors.incotermMultiPortType}
                    >
                      <option value=''>Please select</option>
                      {MULTIPORT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <CounterFieldError message={errors.incotermMultiPortType} />
                  </div>
                </Col>
              )}
            </Row>

            <div className='mb-3'>
              <span className='badge bg-primary-subtle text-primary p-2 px-3 fs-13 fw-medium rounded-2'>
                Total Quantity:{' '}
                <span className='fw-bold ms-1'>
                  {formatParcelSize(formData.deliveryBasis, 'MT')}
                </span>
              </span>
            </div>

            <Row className='g-3 mb-4'>
              <Col md={isFobIncoterm ? 6 : 4}>
                {renderLocationBlock({
                  label:
                    showLocation2 || showLocation3 ? 'Location 1' : 'Location',
                  locationValue: formData.incotermLocationId,
                  locationField: 'incotermLocationId',
                  multiSelectField: 'incotermFOB',
                  dateFromValue: formData.startDate,
                  dateFromField: 'startDate',
                  dateToValue: formData.endDate,
                  dateToField: 'endDate',
                  showQuantity: false,
                  isMultiSelect: isFobIncoterm,
                  multiSelectValue: formData.incotermFOB,
                })}
              </Col>

              {showLocation2 && (
                <Col md={4}>
                  {renderLocationBlock({
                    label: 'Location 2',
                    locationValue: formData.incotermLocation2Id,
                    locationField: 'incotermLocation2Id',
                    dateFromValue: formData.startDate2,
                    dateFromField: 'startDate2',
                    dateToValue: formData.endDate2,
                    dateToField: 'endDate2',
                    showQuantity: false,
                  })}
                </Col>
              )}

              {showLocation3 && (
                <Col md={4}>
                  {renderLocationBlock({
                    label: 'Location 3',
                    locationValue: formData.incotermLocation3Id,
                    locationField: 'incotermLocation3Id',
                    dateFromValue: formData.startDate3,
                    dateFromField: 'startDate3',
                    dateToValue: formData.endDate3,
                    dateToField: 'endDate3',
                    showQuantity: false,
                  })}
                </Col>
              )}
            </Row>

            <div className='p-3 bg-light rounded-3 border-0 fs-13 fw-medium text-dark mb-1'>
              <span className='text-muted me-2'>Timeline:</span> {timelineLabel}
            </div>

            {requiresCargoRatio ? (
              <div className='cargo-ratio-selector border rounded-3 p-3 mb-3 bg-light-subtle'>
                <h5 className='cargo-ratio-selector__title fw-semibold mb-1'>
                  Cargo Ratio Selection
                </h5>
                <p className='cargo-ratio-selector__hint small mb-3 text-muted'>
                  Please select the cargo ratio to proceed.
                </p>
                <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
                  {RATIO_OPTIONS.map(option => {
                    const isSelected = isRatioSelected(selectedRatios, option);

                    return (
                      <button
                        key={option.id}
                        type='button'
                        className={`btn btn-sm px-3 py-2 border rounded-3 d-flex d-md-inline-flex align-items-center gap-2 w-100 w-md-auto transition-all ${
                          isSelected
                            ? 'border-primary bg-primary-subtle text-primary fw-semibold'
                            : 'border-secondary-subtle bg-white text-dark shadow-sm'
                        }`}
                        onClick={() => handleToggleRatio(option)}
                      >
                        <span
                          className={`rounded-circle border d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                            isSelected
                              ? 'border-primary bg-primary text-white'
                              : 'border-secondary bg-light'
                          }`}
                          style={{ width: 18, height: 18 }}
                        >
                          {isSelected ? (
                            <i className='bx bx-check fs-12 lh-1'></i>
                          ) : null}
                        </span>
                        <span className='fw-medium text-start'>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <CounterFieldError message={errors.selectedRatios} />
              </div>
            ) : null}
          </>
        )}
      </ModalBody>

      <CounterModalFooter
        loading={loading}
        saveDisabled={saveDisabled}
        onCancel={toggle}
        onSave={onSave}
        formData={formData}
      />
    </Modal>
  );
};

export default CounterOfferModalLpg;
